export interface TypographyConfig {
  headingFont?: string;
  bodyFont?: string;
  primaryText?: string;
  secondaryText?: string;
  accentColor?: string;
}

/**
 * CSS variable declarations that belong on the scope root (`:root` or the
 * portal container).
 */
export function buildTypographyVarDeclarations(
  config: TypographyConfig,
): string[] {
  const declarations: string[] = [];

  const write = (value: string | undefined, targets: readonly string[]) => {
    if (!value) {
      return;
    }
    for (const target of targets) {
      declarations.push(`${target}: ${value};`);
    }
  };

  write(config.accentColor, [
    "--redo-primary-color",
    "--redo-primary-button-color",
  ]);
  write(config.headingFont, ["--return-app-title-font-family"]);
  write(config.bodyFont, ["--return-app-body-font-family"]);
  write(config.primaryText, ["--redo-colors-text-text-primary-900"]);
  // The builder exposes only two text-color knobs; `secondary` maps to all
  // four non-primary tiers so merchants aren't managing five colors.
  write(config.secondaryText, [
    "--redo-colors-text-text-secondary-700",
    "--redo-colors-text-text-tertiary-600",
    "--redo-colors-text-text-quaternary-500",
    "--redo-colors-text-text-quinary-400",
  ]);

  return declarations;
}

/** Selector-based typography rules (font-family, color, link color). */
export function buildTypographySelectorRules(
  s: string,
  root: string,
  config: TypographyConfig,
): string[] {
  // The body-font rule sets `font-family` on the scope root AND on the modal
  // scope so body-portaled modals (rendered at <body>, outside the
  // customer-portal subtree) still pick up the merchant font.
  const bodyFontRule = config.bodyFont
    ? `${root}, [data-ui="portal-modal"] { font-family: ${config.bodyFont}; }`
    : undefined;

  // Target `body` (not `:root`) in global mode so the rule beats the
  // user-agent `body { color: canvastext }` default. Also pin the merchant
  // primary text on `[data-ui="portal-modal"]` so MUI Paper's hardcoded
  // black `color` doesn't leak through.
  const primaryTextScope = s.trimEnd();
  const primaryTextRule = config.primaryText
    ? `${primaryTextScope || "body"}, [data-ui="portal-modal"] { color: ${config.primaryText}; }`
    : undefined;

  const headingFontRule = config.headingFont
    ? `${s}h1, ${s}h2, ${s}h3, ${s}[data-ui="header"], [data-ui="portal-modal"] h1, [data-ui="portal-modal"] h2, [data-ui="portal-modal"] h3, [data-ui="portal-modal"] [data-ui="header"] { font-family: ${config.headingFont}; }`
    : undefined;

  return [bodyFontRule, primaryTextRule, headingFontRule].filter(
    (rule): rule is string => rule !== undefined,
  );
}
